import { useTranslation } from "react-i18next";
import { X, RotateCcw } from "lucide-react";
import { cn } from "@/utils";
import type { ChatMessage, UsageInfoResponse } from "@/domain/aiAgent";
import { AiAgentMessageList } from "./AiAgentMessageList";
import { AiAgentMessageInput } from "./AiAgentMessageInput";
import { AiAgentUsageBar } from "./AiAgentUsageBar";

interface AiAgentChatPanelProps {
  messages: ChatMessage[];
  isLoading: boolean;
  onSend: (question: string) => void;
  onClose: () => void;
  onClear?: () => void;
  onSubmitFeedback: (
    messageId: string,
    rating: "positive" | "negative",
    reason?: string,
    comment?: string,
  ) => void;
  usageInfo?: UsageInfoResponse | null;
  quotaExceeded?: boolean;
  hasUserback?: boolean;
}

export const AiAgentChatPanel = ({
  messages,
  isLoading,
  onSend,
  onClose,
  onClear,
  onSubmitFeedback,
  usageInfo,
  quotaExceeded,
  hasUserback,
}: AiAgentChatPanelProps) => {
  const { t } = useTranslation();

  return (
    <div className={cn("ai-chat-panel", hasUserback ? "ai-chat-panel--offset" : "")}>
      <div className="ai-chat-header">
        <span className="ai-chat-header-title">{t("agent.title")}</span>
        <div className="flex items-center gap-0.5" role="toolbar" aria-label={t("agent.title")}>
          {onClear && (
            <button
              onClick={onClear}
              aria-label={t("agent.new_chat")}
              className="ai-chat-header-btn"
              title={t("agent.new_chat")}
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
          <button onClick={onClose} aria-label={t("agent.close")} className="ai-chat-header-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <AiAgentMessageList
        messages={messages}
        isLoading={isLoading}
        onExampleClick={onSend}
        onSubmitFeedback={onSubmitFeedback}
      />
      <AiAgentUsageBar usageInfo={usageInfo ?? null} quotaExceeded={!!quotaExceeded} />
      <AiAgentMessageInput onSend={onSend} disabled={isLoading || !!quotaExceeded} />
    </div>
  );
};
